import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import authService from "../../services/api/authService";
import { extractAxiosErrorMessage } from "../../services/utils/errorUtils";
import { normalizeAuthResponse } from "../../services/utils/pinAuthUtils";
import { useCallback } from "react";
import { QUERY_CONFIGS } from "../../lib/react-query/config";
import useAuthStore from "../../stores/authStore";
import profileService from "../../services/api/profileService";

export const authQueryKeys = {
  all: ["auth"],
  login: () => [...authQueryKeys.all, "login"],
  mfa: () => [...authQueryKeys.all, "mfa"],
  resendMfa: () => [...authQueryKeys.all, "resendMfa"],
  requestPasswordReset: () => [...authQueryKeys.all, "requestPasswordReset"],
  requestReactivation: () => [...authQueryKeys.all, "requestReactivation"],
  resetPassword: () => [...authQueryKeys.all, "resetPassword"],
  activateAccount: () => [...authQueryKeys.all, "activateAccount"],
  status: () => [...authQueryKeys.all, "status"],
  currentUser: () => [...authQueryKeys.all, "currentUser"],
  tokenValid: (token) => [...authQueryKeys.all, "tokenValid", token],
  verifyPin: () => [...authQueryKeys.all, "verifyPin"],
  enrollPin: () => [...authQueryKeys.all, "enrollPin"],
  setupPin: () => [...authQueryKeys.all, "setupPin"],
  changePin: () => [...authQueryKeys.all, "changePin"],
  requestPinReset: () => [...authQueryKeys.all, "requestPinReset"],
  resetPin: () => [...authQueryKeys.all, "resetPin"],
};

export const useLoginMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (credentials) =>
      authService
        .login(credentials.email, credentials.password)
        .then((response) => normalizeAuthResponse(response.data)),
    mutationKey: authQueryKeys.login(),
    onSuccess: () => {
      queryClient.invalidateQueries(authQueryKeys.status());
    },
    retry: false,
  });
};

export const useVerifyPinMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ challengeId, pin }) =>
      authService
        .verifyPin(challengeId, pin)
        .then((response) => normalizeAuthResponse(response.data)),
    mutationKey: authQueryKeys.verifyPin(),
    onSuccess: () => {
      queryClient.invalidateQueries(authQueryKeys.status());
    },
    retry: false,
  });
};

export const useEnrollPinMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ challengeId, newPin, confirmPin }) =>
      authService
        .enrollPinWithChallenge(challengeId, newPin, confirmPin)
        .then((response) => normalizeAuthResponse(response.data)),
    mutationKey: authQueryKeys.enrollPin(),
    onSuccess: () => {
      queryClient.invalidateQueries(authQueryKeys.status());
    },
    retry: false,
  });
};

export const useSetupPinMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ newPin, confirmPin }) =>
      profileService.setupPin(newPin, confirmPin),
    mutationKey: authQueryKeys.setupPin(),
    onSuccess: () => {
      queryClient.invalidateQueries(authQueryKeys.currentUser());
    },
    retry: false,
  });
};

export const useChangePinMutation = () => {
  return useMutation({
    mutationFn: ({ currentPin, newPin, confirmPin }) =>
      profileService.changePin(currentPin, newPin, confirmPin),
    mutationKey: authQueryKeys.changePin(),
    retry: false,
  });
};

export const useRequestPinReset = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (email) =>
      authService
        .requestPinReset(email)
        .then((response) => normalizeAuthResponse(response.data)),
    mutationKey: authQueryKeys.requestPinReset(),
    onSuccess: () => {
      queryClient.invalidateQueries(authQueryKeys.status());
    },
    retry: false,
  });
};

export const useResetPin = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ token, newPin, confirmPin }) =>
      authService
        .resetPin(token, newPin, confirmPin)
        .then((response) => response.data),
    mutationKey: authQueryKeys.resetPin(),
    onSuccess: () => {
      queryClient.invalidateQueries(authQueryKeys.status());
    },
    retry: false,
  });
};

export const useVerifyMfaMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (token) =>
      authService.verifyMfaCode(token).then((response) => response),
    mutationKey: authQueryKeys.mfa(),
    onSuccess: () => {
      queryClient.invalidateQueries(authQueryKeys.status());
    },
    retry: false,
  });
};

export const useResendMfaCodeMutation = () => {
  const request = useCallback(async (email) => {
    try {
      const response = await authService.resendMfaCode(email);
      return response.data;
    } catch (error) {
      const errorMessage = extractAxiosErrorMessage(error, "Resend OTP failed");
      throw new Error(errorMessage);
    }
  }, []);

  return useMutation({
    mutationFn: request,
    mutationKey: authQueryKeys.resendMfa(),
    retry: false,
  });
};

export const useRequestPasswordReset = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (email) =>
      authService.requestPasswordReset(email).then((response) => response.data),
    mutationKey: authQueryKeys.requestPasswordReset(),
    onSuccess: () => {
      queryClient.invalidateQueries(authQueryKeys.status());
    },
    retry: false,
  });
};

export const useResetPassword = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ token, password }) =>
      authService
        .resetPassword(token, password)
        .then((response) => response.data),
    mutationKey: authQueryKeys.resetPassword(),
    onSuccess: () => {
      queryClient.invalidateQueries(authQueryKeys.status());
    },
    retry: false,
  });
};

export const useActivateAccount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ token, password, confirmPassword, newPin, confirmPin }) =>
      authService
        .activateAccount(token, {
          password,
          confirmPassword,
          newPin,
          confirmPin,
        })
        .then((response) => response.data),
    mutationKey: authQueryKeys.activateAccount(),
    onSuccess: () => {
      queryClient.invalidateQueries(authQueryKeys.status());
    },
    retry: false,
  });
};

export const useRequestReactivation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (email) =>
      authService.requestReactivation(email).then((response) => response.data),
    mutationKey: authQueryKeys.requestReactivation(),
    onSuccess: () => {
      queryClient.invalidateQueries(authQueryKeys.status());
    },
    retry: false,
  });
};

export const useCheckTokenValidQuery = (token) => {
  return useQuery({
    queryKey: authQueryKeys.tokenValid(token),
    queryFn: () => authService.checkTokenValid(token).then((res) => res.data),
    enabled: !!token,
    retry: false,
    staleTime: Infinity,
    gcTime: 0,
    refetchOnWindowFocus: false,
  });
};

export const useCurrentUserQuery = (options = {}) => {
  const setComputedUser = useAuthStore((state) => state.setComputedUser);

  const request = async () => {
    const response = await profileService.getCurrentUser();
    if (response && typeof setComputedUser === "function") {
      setComputedUser(response);
    }
    return response;
  };

  return useQuery({
    queryKey: authQueryKeys.currentUser(),
    queryFn: request,
    ...QUERY_CONFIGS.CRITICAL,
    throwOnError: false,
    ...options,
  });
};

export default useCurrentUserQuery;
